import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import CanonicalTag from "@/components/CanonicalTag";
import homeschoolClassroomImage from "@/assets/homeschool-classroom-adu.jpg";

const HowToUseADUHomeschoolClassroom = () => {
  const [showCalendly, setShowCalendly] = useState(false);

  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-home-school-classroom" />
      <Navigation />
      
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#E4683B' }}>
            How to Use Your ADU as a Classroom, Homework Hub or Homeschool Studio
          </h1>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
            Build a Learning Sanctuary at Home
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-3xl mx-auto">
            A backyard ADU classroom supports homeschooling, tutoring, and creative education in a quiet, dedicated space.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button 
              onClick={openCalendlyModal}
              className="bg-[#E4683B] hover:bg-[#E4683B]/90 text-white px-8 py-3 text-lg"
            >
              📅 Book & Schedule
            </Button>
            <Button 
              variant="outline" 
              className="border-[#E4683B] text-white bg-[#E4683B] hover:bg-[#E4683B]/90 px-8 py-3 text-lg"
              onClick={() => window.location.href = '/landing'}
            >
              📐 What fits my lot?
            </Button>
          </div>
        </div>

        {/* Main Image */}
        <div className="mb-16">
          <img 
            src={homeschoolClassroomImage} 
            alt="Dedicated ADU homeschool classroom with learning stations" 
            className="w-full max-w-4xl mx-auto rounded-lg shadow-lg"
          />
        </div>

        {/* Educational Features Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#E4683B' }}>
            Educational Features
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-card p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Desk zones, whiteboard walls</h3>
              <p className="text-muted-foreground">Dedicated learning stations with interactive teaching surfaces.</p>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Soft flooring for comfort</h3>
              <p className="text-muted-foreground">Child-friendly surfaces that support various learning activities.</p>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Natural light for focus</h3>
              <p className="text-muted-foreground">Optimal lighting conditions that enhance concentration and well-being.</p>
            </div>
          </div>
        </div>

        {/* Mid-Section CTAs */}
        <div className="text-center mb-16">
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            variant="outline" 
            className="border-[#E4683B] text-white bg-[#E4683B] hover:bg-[#E4683B]/90 px-8 py-3"
            onClick={() => window.location.href = '/landing'}
          >
            📐 What fits my lot?
          </Button>
          <Button 
            className="bg-white text-[#E4683B] border border-[#E4683B] hover:bg-[#E4683B] hover:text-white px-8 py-3"
            onClick={() => window.location.href = '/adu-calculator'}
          >
            💰 Get Price & ROI Report
          </Button>
        </div>
        </div>

        {/* Flexible Learning Uses Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#E4683B' }}>
            Flexible Learning Uses
          </h2>
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-muted-foreground text-center">
              Ideal for one-on-one tutoring, co-op groups, language classes, or enrichment programs
            </p>
          </div>
        </div>

        {/* Value Proposition Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#E4683B' }}>
            Value Proposition
          </h2>
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-muted-foreground text-center">
              A learning ADU supports education, child development, and family time—all while enhancing property value.
            </p>
          </div>
        </div>

        {/* Bottom CTAs */}
        <div className="text-center">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="outline" 
              className="border-[#E4683B] text-[#E4683B] hover:bg-[#E4683B] hover:text-white px-8 py-3"
              onClick={() => window.open('/our-work', '_blank')}
            >
              🏡 Our Portfolio
            </Button>
            <Button 
              variant="outline" 
              className="border-[#E4683B] text-[#E4683B] hover:bg-[#E4683B] hover:text-white px-8 py-3"
              onClick={() => window.open('/faq', '_blank')}
            >
              ❓ Our FAQ
            </Button>
          </div>
        </div>
      </div>

      <Footer />

      {/* Calendly Modal */}
      <Dialog open={showCalendly} onOpenChange={setShowCalendly}>
        <DialogContent className="max-w-4xl max-h-[80vh] p-0">
          <div className="relative w-full h-[600px]">
            <button
              onClick={closeCalendlyModal}
              className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100"
            >
              ✕
            </button>
            <iframe
              src="https://calendly.com/udaadu"
              width="100%"
              height="100%"
              frameBorder="0"
              title="Schedule a consultation"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HowToUseADUHomeschoolClassroom;